import { Box, Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import {
  CardActionRow,
  CardCredentialsModal,
  CardHistoryBlock,
  CardLimitsBlock,
  CardSettingsDrawer,
  CardVisual,
  RevealPinDrawer,
} from "../../components/pages/cards";
import { Skeleton, WebBackButton } from "../../components/shared";
import { useTelegram } from "../../hooks";
import {
  EyeIcon,
  FreezeIcon,
  PlusCircleIcon,
  SettingsIcon,
} from "../../icons";
import { CardStatus } from "../../services/cards/mocks";
import {
  getCardDetails,
  getMyCards,
  type CardSensitiveDetails,
} from "../../services/cards";

export function CardDetailPage() {
  const { cardId } = useParams<{ cardId: string }>();
  const navigate = useNavigate();
  const { tg } = useTelegram();
  const { t } = useTranslation("cards");
  const isMobile =
    tg.platform !== "tdesktop" &&
    tg.platform !== "weba" &&
    tg.platform !== "macos";

  const { data: cards, isLoading: cardsLoading } = useQuery({
    queryFn: getMyCards,
    queryKey: ["my-cards"],
  });

  const card = useMemo(
    () => cards?.find((c) => c.id === (cardId ?? "")),
    [cards, cardId]
  );

  const [status, setStatus] = useState<CardStatus | null>(card?.status ?? null);
  // Реквизиты храним только в локальном state, без кэша react-query:
  // пользователь обязан вводить PIN на каждый показ, поэтому в момент
  // «закрыть модалку» / выхода со страницы данные должны исчезнуть.
  const [details, setDetails] = useState<CardSensitiveDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [revealPinOpen, setRevealPinOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isLoading = cardsLoading;

  useEffect(() => {
    if (card && status === null) setStatus(card.status);
  }, [card, status]);

  useEffect(() => {
    tg.BackButton.show();
    const handler = () => navigate("/cards");
    tg.onEvent("backButtonClicked", handler);
    return () => {
      tg.BackButton.hide();
      tg.offEvent("backButtonClicked", handler);
    };
  }, [navigate, tg]);

  // Держим все хуки до любых ранних `return` (Rules of Hooks).
  const handleRevealPinSubmit = useCallback(
    async (pin: string) => {
      if (!cardId) return;
      // Ошибки (401/429/…) пробрасываем в drawer — он покажет message и очистит поле.
      const data = await getCardDetails(cardId, pin);
      setDetails(data);
      setRevealPinOpen(false);
      // Реквизиты открываем в модалке (bottom-drawer на мобиле,
      // центрированный Dialog на десктопе). На детальном экране
      // они больше не отображаются inline.
      setDetailsOpen(true);
    },
    [cardId],
  );

  const handleCloseDetailsModal = useCallback(() => {
    setDetailsOpen(false);
    // Небольшая задержка — даём Drawer/Dialog доиграть закрывающую анимацию,
    // прежде чем реально стереть чувствительные данные, иначе последний
    // кадр покажет пустые «•••».
    setTimeout(() => setDetails(null), 220);
  }, []);

  if (cardsLoading) {
    return (
      <Box sx={{ width: "100%", marginTop: "20px" }}>
        <Skeleton
          variant="rounded"
          sx={{ borderRadius: "20px", aspectRatio: "500 / 295", width: "100%" }}
        />
      </Box>
    );
  }
  if (!card) {
    return <Navigate to="/cards" replace />;
  }

  const effectiveStatus = status ?? card.status;
  const isProcessing = effectiveStatus === "processing";
  const isFrozen = effectiveStatus === "frozen";

  const handleToggleFreeze = () => {
    if (isProcessing) return;
    setStatus((prev) => (prev === "frozen" ? "active" : "frozen"));
    if (isFrozen) {
      enqueueSnackbar(t("actions.unfrozen"), { variant: "success" });
    } else {
      enqueueSnackbar(t("actions.frozen"), {
        variant: "success",
      });
      // При заморозке сразу закрываем реквизиты, если они были открыты.
      setDetailsOpen(false);
      setDetails(null);
    }
  };

  const handleRevealClick = () => {
    if (isProcessing) return;
    if (isFrozen) {
      enqueueSnackbar(t("actions.unfreeze_to_reveal"), {
        variant: "info",
      });
      return;
    }
    setRevealPinOpen(true);
  };

  const handleTopup = () => {
    // Виртуальная карта — реального PIN нет, но пополнение планируется через
    // внутренний баланс USDT. Пока заглушка: показываем инфо-снек и не
    // блокируем поток. Как только появится /me/cards/:id/topup — сюда
    // подставится настоящий вызов.
    if (isProcessing) {
      enqueueSnackbar(t("actions.pin_after_issue"), { variant: "info" });
      return;
    }
    if (isFrozen) {
      enqueueSnackbar(t("actions.topup_frozen"), { variant: "info" });
      return;
    }
    enqueueSnackbar(t("actions.topup_soon"), { variant: "info" });
  };

  const handleBlock = () => {
    enqueueSnackbar(t("actions.blocked"), {
      variant: "success",
    });
    setTimeout(() => navigate("/cards"), 400);
  };

  // Порядок: сверху пара «активных» действий (пополнить + посмотреть
  // реквизиты), снизу — «управление» (заморозить + настройки). На десктопе
  // в 2×2 это даёт логичное разделение: слева-верх «касса», справа-верх
  // «данные», слева-низ «доступ/безопасность», справа-низ «прочее».
  // «Сменить PIN» убрали: карта виртуальная, физического PIN нет.
  const actions = [
    {
      id: "topup",
      label: t("actions.topup"),
      icon: <PlusCircleIcon color="currentColor" />,
      onClick: handleTopup,
      disabled: isProcessing,
    },
    {
      id: "reveal",
      label: t("actions.details"),
      icon: <EyeIcon color="currentColor" />,
      onClick: handleRevealClick,
      disabled: isProcessing,
    },
    {
      id: "freeze",
      label: isFrozen ? t("unfreeze") : t("freeze"),
      icon: <FreezeIcon color={isFrozen ? "#8AB4FF" : "currentColor"} />,
      onClick: handleToggleFreeze,
      disabled: isProcessing,
      active: isFrozen,
    },
    {
      id: "settings",
      label: t("actions.settings"),
      icon: <SettingsIcon color="currentColor" />,
      onClick: () => setSettingsOpen(true),
    },
  ];

  const processingBanner = (
    <Box
      sx={{
        padding: "14px 16px",
        borderRadius: "18px",
        bgcolor: "rgba(234, 194, 0, 0.08)",
        border: "1px solid rgba(234, 194, 0, 0.25)",
      }}
    >
      <Typography sx={{ fontSize: "14px", fontWeight: 600, marginBottom: "4px" }}>
        {t("processing.title")}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.5 }}>
        {t("processing.subtitle")}
      </Typography>
    </Box>
  );

  const cardVisual = isLoading ? (
    <Skeleton
      variant="rounded"
      sx={{ borderRadius: "20px", aspectRatio: "500 / 295", width: "100%" }}
    />
  ) : (
    <CardVisual card={card} size="large" statusOverride={effectiveStatus} />
  );

  return (
    <Box
      sx={{
        display: "grid",
        gap: "16px",
        width: "100%",
        marginTop: isMobile ? "40px" : "20px",
        marginBottom: "120px",
      }}
    >
      <WebBackButton to="/cards" title={card.name} />

      {/*
        Основная сетка страницы карты.
        - На мобильном (`xs`) — всё одной колонкой: карта, действия, реквизиты,
          лимиты — как раньше.
        - На десктопе (>= `md`) — двухколоночный grid: слева карта (не
          растягивается на всю ширину, потому что колонка ~45%),
          справа — панель действий + процессинг-баннер. Реквизиты и лимиты
          остаются под сеткой на всю ширину — они длинные и рядом с картой
          смотрелись бы куце.
       */}
      <Box
        sx={{
          display: "grid",
          gap: "16px",
          alignItems: "start",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 1.1fr) minmax(0, 1fr)",
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>{cardVisual}</Box>

        <Box
          sx={{
            display: "grid",
            gap: "16px",
            minWidth: 0,
            // На мобилке actions/баннер идут прямо под картой ровно так же,
            // как было — просто gap другого уровня grid'а сработает.
          }}
        >
          {isProcessing ? (
            processingBanner
          ) : (
            // На мобиле actions в один ряд (4 в строку), на десктопе — 2×2,
            // потому что правая колонка узкая и 4 в строку сжимаются некрасиво.
            <CardActionRow
              actions={actions}
              columns={{ xs: actions.length, md: 2 }}
            />
          )}
        </Box>
      </Box>

      {/*
        История операций по карте — вместо inline-блока с реквизитами.
        Сами реквизиты (PAN/CVV/expiry) теперь показываются в модалке
        после успешного ввода PIN — см. `CardCredentialsModal` ниже.
       */}
      {!isProcessing && <CardHistoryBlock cardId={card.id} />}

      {!isProcessing && (
        <CardLimitsBlock limits={card.limits} isLoading={isLoading} />
      )}

      <RevealPinDrawer
        open={revealPinOpen}
        onClose={() => setRevealPinOpen(false)}
        onSubmit={handleRevealPinSubmit}
      />
      <CardCredentialsModal
        open={detailsOpen}
        onClose={handleCloseDetailsModal}
        card={card}
        details={details}
      />
      <CardSettingsDrawer
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        card={card}
        onBlock={handleBlock}
      />
    </Box>
  );
}
